import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Testimonials from '../components/Testimonials';
import CTA from '../components/Cta';
import { useSEO } from '../hooks/useSEO';

const DigitalWallet = () => {
  const [activeWallet, setActiveWallet] = useState('apple');
  const [showComingSoonPopup, setShowComingSoonPopup] = useState(false);
  const [selectedCard, setSelectedCard] = useState('personal');
  const [walletCount, setWalletCount] = useState(50);
  
  useSEO({
    title: "Digital Wallet Cards | Add to Apple & Google Wallet | AM CARDMATE",
    description: "Add your digital business card directly to Apple Wallet and Google Wallet. Share instantly with a tap, no app needed.",
    image: "/assets/digitalwallet.jpg",
    keywords: "digital wallet, apple wallet, google wallet, pass, pkpass, contactless sharing"
  });

  const walletSteps = [
    {
      number: '1',
      title: 'Create Your Card',
      description: 'Design your digital business card in the AM CARDMATE app',
      icon: 'palette'
    },
    {
      number: '2',
      title: 'Tap Add to Wallet',
      description: 'One-tap installation to Apple Wallet or Google Wallet',
      icon: 'download'
    },
    {
      number: '3',
      title: 'Share Instantly',
      description: 'Open your wallet and share with anyone nearby',
      icon: 'share-alt'
    }
  ];

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "AM CARDMATE Digital Wallet",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "iOS, Android",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "GBP"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "ratingCount": "1200"
        },
        "featureList": [
          "Apple Wallet Integration",
          "Google Wallet Integration",
          "Offline Access",
          "Instant Sharing"
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to Add Digital Business Card to Wallet",
        "description": "Step-by-step guide to add your card to digital wallets",
        "step": walletSteps.map((step, index) => ({
          "@type": "HowToStep",
          "position": index + 1,
          "name": step.title,
          "text": step.description
        }))
      }
    ];
    
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);

  const walletFeatures = [
    {
      icon: 'bolt',
      title: 'Instant Access',
      description: 'Access your card directly from your phone\'s wallet - no app needed, always ready to share.',
      gradient: 'from-blue-400 to-blue-600'
    },
    {
      icon: 'shield-alt',
      title: 'Secure & Private',
      description: 'Protected by your device\'s built-in security. Your data stays safe and encrypted.',
      gradient: 'from-green-400 to-green-600'
    },
    {
      icon: 'sync',
      title: 'Offline Ready',
      description: 'No signal? No problem. Your card is always stored locally in your phone\'s wallet.',
      gradient: 'from-purple-400 to-purple-600'
    },
    {
      icon: 'globe',
      title: 'Works Everywhere',
      description: 'Compatible with billions of devices worldwide. Share with anyone, anywhere.',
      gradient: 'from-pink-400 to-pink-600'
    }
  ];

  const cardTypes = [
    {
      id: 'personal',
      title: 'Personal Card',
      description: 'Perfect for individual professionals',
      icon: 'user',
      features: [
        'Create Personalised Digital Cards',
        'Instant Share via QR or NFC',
        'Organise & Save Contacts',
        'Auto Sync Your Info',
        'Insightful View Tracking'
      ],
      color: 'from-blue-400 to-blue-600',
      available: true
    },
    {
      id: 'business',
      title: 'Business Card',
      description: 'Ideal for entrepreneurs & teams',
      icon: 'briefcase',
      features: [
        'Build Custom Branded Cards',
        'Smart Sharing (QR/NFC)',
        'Manage Team & Contacts',
        'Real-time Card Updates',
        'Engagement Analytics'
      ],
      color: 'from-purple-400 to-purple-600',
      available: true
    },
    {
      id: 'enterprise',
      title: 'Enterprise Card',
      description: 'Complete solution for organizations',
      icon: 'building',
      features: ['Unlimited Team Members', 'API Integration', 'Dedicated Account Manager', 'Custom Development', 'White-label Solutions', 'Enterprise Security'],
      color: 'from-green-400 to-green-600',
      available: false
    }
  ];

  const calculateSavings = () => {
    const costPerCard = 0.15;
    const designCost = 50;
    const shippingCost = 15;
    const digitalCost = 0;
    const physicalCost = (walletCount * costPerCard) + (designCost / 12) + (shippingCost / 12);
    const savings = physicalCost;
    const savingsPercentage = 100;
    
    return {
      physicalCost: physicalCost.toFixed(2),
      digitalCost: digitalCost.toFixed(2),
      savings: savings.toFixed(2),
      savingsPercentage
    };
  };

  const savings = calculateSavings();

  return (
    <>
      <section className="pt-20 md:pt-24 lg:pt-32 pb-32 bg-gradient-to-br from-white via-blue-50/20 to-purple-50/20 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-[1200px] h-[1200px] bg-gradient-to-br from-blue-300/10 via-purple-300/10 to-pink-300/10 rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[1000px] h-[1000px] bg-gradient-to-tl from-green-300/10 via-blue-300/10 to-purple-300/10 rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto mb-10 md:mb-16">
            <div className="badge badge-purple inline-flex mb-6">
              <FontAwesomeIcon icon="wallet" />
              <span>Digital Wallet Integration</span>
            </div>
            
            <h1 className="text-h1 mb-6">
              Your Business Card in{' '}
              <span className="text-gradient-blue-pink">Every Pocket</span>
            </h1>
            
            <p className="text-body-lg text-gray mb-8 leading-relaxed max-w-3xl mx-auto">
              Add your digital business card to Apple Wallet and Google Wallet. Share instantly without opening any app - just like a contactless payment.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setShowComingSoonPopup(true)}
                className="btn btn-primary group w-full sm:w-auto"
              >
                <FontAwesomeIcon icon="download" />
                <span>Download the App</span>
                <FontAwesomeIcon 
                  icon="arrow-right" 
                  className="ml-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" 
                />
              </button>
            </div>
          </div>
          
          <div className="relative max-w-6xl mx-auto">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl blur-3xl"></div>
              
              <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-8 md:p-12 shadow-2xl overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full">
                  <div className="absolute top-10 left-10 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl"></div>
                  <div className="absolute bottom-10 right-10 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl"></div>
                </div>
                
                <div className="relative z-10">
                  <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
                    <div className="flex-1 text-center lg:text-left">
                      <h2 className="text-h2 text-white mb-4">
                        One Card, Two Wallets, <span className="text-gradient-yellow-pink">Infinite Possibilities</span>
                      </h2>
                      <p className="text-body-lg text-gray-300 mb-8">
                        Your AM CARDMATE digital business card works seamlessly with both Apple Wallet and Google Wallet. No more fumbling with apps at networking events.
                      </p>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto lg:mx-0">
                        <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-5 py-4 rounded-2xl hover:bg-white/20 transition-all cursor-pointer">
                          <FontAwesomeIcon icon={['fab', 'apple']} className="text-white text-3xl" />
                          <div className="text-left">
                            <div className="text-caption text-gray-400">Works with</div>
                            <div className="text-body text-white font-bold">Apple Wallet</div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-5 py-4 rounded-2xl hover:bg-white/20 transition-all cursor-pointer">
                          <FontAwesomeIcon icon={['fab', 'google']} className="text-white text-3xl" />
                          <div className="text-left">
                            <div className="text-caption text-gray-400">Works with</div>
                            <div className="text-body text-white font-bold">Google Wallet</div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-8 grid grid-cols-3 gap-4 max-w-md mx-auto lg:mx-0">
                        <div className="text-center">
                          <div className="text-h4 text-white mb-1">2B+</div>
                          <div className="text-caption text-gray-400">Compatible Devices</div>
                        </div>
                        <div className="text-center">
                          <div className="text-h4 text-white mb-1">0.3s</div>
                          <div className="text-caption text-gray-400">Share Time</div>
                        </div>
                        <div className="text-center">
                          <div className="text-h4 text-white mb-1">100%</div>
                          <div className="text-caption text-gray-400">Success Rate</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="relative">
                      <div className="relative w-80 h-[650px]">
                        <div
                          className="absolute inset-0"
                          style={{ 
                            transformStyle: 'preserve-3d',
                            transform: activeWallet === 'apple' ? 'rotateY(0deg)' : 'rotateY(180deg)',
                            transition: 'transform 0.6s'
                          }}
                        >
                          <div className="absolute inset-0" style={{ backfaceVisibility: 'hidden' }}>
                            <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-6 h-full">
                              <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-4 mb-4">
                                <div className="flex items-center justify-between text-white">
                                  <span className="text-body font-bold">Apple Wallet</span>
                                  <FontAwesomeIcon icon={['fab', 'apple']} className="text-2xl" />
                                </div>
                              </div>
                              <div className="space-y-4">
                                <div className="bg-gray-100 rounded-xl p-4 flex items-center gap-4">
                                  <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-xl flex items-center justify-center text-white font-bold text-h5">
                                    JD
                                  </div>
                                  <div>
                                    <div className="text-body font-bold text-gray-900">John Doe</div>
                                    <div className="text-body-sm text-gray-600">Creative Director</div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  <button className="bg-blue-100 text-blue-600 p-3 rounded-xl text-body-sm font-medium hover:bg-blue-200 transition-colors">
                                    <FontAwesomeIcon icon="phone" className="mb-1" />
                                    <div>Call</div>
                                  </button>
                                  <button className="bg-purple-100 text-purple-600 p-3 rounded-xl text-body-sm font-medium hover:bg-purple-200 transition-colors">
                                    <FontAwesomeIcon icon="envelope" className="mb-1" />
                                    <div>Email</div>
                                  </button>
                                  <button className="bg-pink-100 text-pink-600 p-3 rounded-xl text-body-sm font-medium hover:bg-pink-200 transition-colors">
                                    <FontAwesomeIcon icon="globe" className="mb-1" />
                                    <div>Web</div>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="absolute inset-0" style={{ transform: 'rotateY(180deg)', backfaceVisibility: 'hidden' }}>
                            <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-6 h-full">
                              <div className="bg-gradient-to-r from-[#4285F4] via-[#EA4335] to-[#FBBC04] rounded-2xl p-4 mb-4">
                                <div className="flex items-center justify-between text-white">
                                  <span className="text-body font-bold">Google Wallet</span>
                                  <FontAwesomeIcon icon={['fab', 'google']} className="text-2xl" />
                                </div>
                              </div>
                              <div className="space-y-4">
                                <div className="bg-gray-100 rounded-xl p-4 flex items-center gap-4">
                                  <div className="w-16 h-16 bg-gradient-to-br from-[#4285F4] to-[#34A853] rounded-xl flex items-center justify-center text-white font-bold text-h5">
                                    JD
                                  </div>
                                  <div>
                                    <div className="text-body font-bold text-gray-900">John Doe</div>
                                    <div className="text-body-sm text-gray-600">Creative Director</div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  <button className="bg-blue-100 text-[#4285F4] p-3 rounded-xl text-body-sm font-medium hover:bg-blue-200 transition-colors">
                                    <FontAwesomeIcon icon="phone" className="mb-1" />
                                    <div>Call</div>
                                  </button>
                                  <button className="bg-red-100 text-[#EA4335] p-3 rounded-xl text-body-sm font-medium hover:bg-red-200 transition-colors">
                                    <FontAwesomeIcon icon="envelope" className="mb-1" />
                                    <div>Email</div>
                                  </button>
                                  <button className="bg-green-100 text-[#34A853] p-3 rounded-xl text-body-sm font-medium hover:bg-green-200 transition-colors">
                                    <FontAwesomeIcon icon="globe" className="mb-1" />
                                    <div>Web</div>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-4">
                          <button
                            onClick={() => setActiveWallet('apple')}
                            className={`px-6 py-3 rounded-full font-bold transition-all transform hover:scale-105 text-body ${
                              activeWallet === 'apple' 
                                ? 'bg-white text-gray-900 shadow-xl scale-105' 
                                : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                            }`}
                          >
                            <FontAwesomeIcon icon={['fab', 'apple']} className="mr-2 text-lg" />
                            Apple
                          </button>
                          
                          <button
                            onClick={() => setActiveWallet('google')}
                            className={`px-6 py-3 rounded-full font-bold transition-all transform hover:scale-105 text-body ${
                              activeWallet === 'google' 
                                ? 'bg-white text-gray-900 shadow-xl scale-105' 
                                : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                            }`}
                          >
                            <FontAwesomeIcon icon={['fab', 'google']} className="mr-2 text-lg" />
                            Google
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -top-6 -right-6 bg-gradient-to-r from-green-400 to-emerald-500 text-gray-900 rounded-2xl px-6 py-3 shadow-xl font-bold z-20 hidden md:block text-body">
                <FontAwesomeIcon icon="gift" className="mr-2" />
                Free Feature
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-20 md:py-32 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
        <div className="absolute top-1/2 -translate-y-1/2 left-0 w-[800px] h-[800px] bg-gradient-to-r from-blue-100/50 to-purple-100/50 rounded-full filter blur-3xl -translate-x-1/2"></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="badge badge-purple inline-flex mb-6">
              <FontAwesomeIcon icon="layer-group" />
              <span>Choose Your Card</span>
            </div>
            
            <h2 className="text-h2 text-dark mb-6">
              Select the Perfect <span className="text-gradient-blue-pink">Digital Card</span> for You
            </h2>
            
            <p className="text-body-lg text-gray max-w-3xl mx-auto">
              From individual professionals to large enterprises, we have the right solution for everyone
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-20">
            {cardTypes.map((card) => (
              <div
                key={card.id}
                onClick={() => card.available && setSelectedCard(card.id)}
                className={`relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 ${
                  card.available ? 'cursor-pointer transform hover:scale-105' : 'opacity-75 cursor-not-allowed'
                } ${
                  selectedCard === card.id && card.available ? 'ring-4 ring-blue-500 ring-opacity-50' : ''
                }`}
              >
                <div className={`w-20 h-20 bg-gradient-to-br ${card.color} rounded-2xl flex items-center justify-center text-white mb-6 mx-auto shadow-lg ${
                  !card.available ? 'opacity-70' : ''
                }`}>
                  <FontAwesomeIcon icon={card.icon} className="text-3xl" />
                </div>
                
                <h3 className="text-h4 text-dark mb-2">{card.title}</h3>
                <p className="text-body text-gray mb-6">{card.description}</p>
                
                <div className="mb-6">
                  {card.available ? (
                    <span className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-body-sm font-bold">
                      <FontAwesomeIcon icon="check-circle" />
                      Free with AM CARDMATE
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 px-4 py-2 rounded-full text-body-sm font-bold shadow-md">
                      <FontAwesomeIcon icon="rocket" />
                      Coming Q1 2026
                    </span>
                  )}
                </div>
                
                <ul className="space-y-3">
                  {card.features.map((feature, idx) => (
                    <li key={idx} className={`flex items-start gap-2 ${card.available ? 'text-gray-700' : 'text-gray-500'}`}>
                      <FontAwesomeIcon icon="check-circle" className={`${card.available ? 'text-green-500' : 'text-gray-400'} mt-0.5 flex-shrink-0`} />
                      <span className="text-body-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                {selectedCard === card.id && card.available && (
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1 rounded-full text-body-sm font-bold">
                    Selected
                  </div>
                )}
                
                {!card.available && (
                  <div className="absolute inset-0 rounded-3xl flex items-center justify-center">
                    <div className="absolute inset-0 bg-white/50 backdrop-blur-sm rounded-3xl"></div>
                    <div className="relative bg-black text-white px-8 py-4 rounded-2xl font-bold shadow-2xl transform -rotate-2">
                      <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon="rocket" className="text-xl" />
                        <span className="text-body-lg">Coming Q1 2026</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="badge badge-blue inline-flex mb-4">
              <FontAwesomeIcon icon="magic" />
              <span>Key Features</span>
            </div>
            
            <h2 className="text-h2 text-dark mb-6">
              Why Add Your Card to <span className="text-gradient-blue-pink">Digital Wallets?</span>
            </h2>
            
            <p className="text-body-lg text-gray max-w-3xl mx-auto">
              Transform how you share contact information with the power of native wallet integration
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {walletFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                    <FontAwesomeIcon icon={feature.icon} className="text-white text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-h5 text-dark mb-2">{feature.title}</h3>
                    <p className="text-body text-gray leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-20 md:py-32 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-5"></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <div className="badge badge-blue inline-flex mb-6">
                <FontAwesomeIcon icon="calculator" />
                <span>ROI Calculator</span>
              </div>
              
              <h2 className="text-h2 text-dark mb-6">
                Calculate Your <span className="text-gradient-blue-pink">Digital Savings</span>
              </h2>
              
              <p className="text-body-lg text-gray max-w-3xl mx-auto">
                See how much you can save by switching from traditional paper business cards to digital wallet cards
              </p>
            </div>
            
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-h4 text-dark mb-8 flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center text-white">
                      <FontAwesomeIcon icon="sliders-h" />
                    </div>
                    Your Usage
                  </h3>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="flex justify-between items-center mb-3">
                        <span className="text-body font-semibold text-dark">Cards shared monthly</span>
                        <span className="text-h4 text-blue-600">{walletCount}</span>
                      </label>
                      <input 
                        type="range" 
                        min="10" 
                        max="500" 
                        value={walletCount}
                        onChange={(e) => setWalletCount(parseInt(e.target.value))}
                        className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${(walletCount - 10) / 490 * 100}%, #E5E7EB ${(walletCount - 10) / 490 * 100}%, #E5E7EB 100%)`
                        }}
                      />
                      <div className="flex justify-between text-body-sm text-gray mt-2">
                        <span>10</span>
                        <span>500</span>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-2xl p-6">
                      <h4 className="text-body font-semibold text-dark mb-4">Traditional Paper Cards</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-body text-gray">Cost per card</span>
                          <span className="text-body font-medium">£0.10</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-body text-gray">Design updates</span>
                          <span className="text-body font-medium">£50/update</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-body text-gray">Environmental impact</span>
                          <span className="text-body font-medium text-red-600">High</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-h4 text-dark mb-8 flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center text-white">
                      <FontAwesomeIcon icon="chart-pie" />
                    </div>
                    Your Savings
                  </h3>
                  
                  <div className="space-y-6">
                    <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8">
                      <div className="text-center mb-6">
                        <div className="text-h1 text-gradient-blue-pink mb-2">
                          {savings.savingsPercentage}%
                        </div>
                        <div className="text-body text-gray">Monthly Savings</div>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-body text-gray">Paper cards cost</span>
                          <span className="text-h5 line-through text-gray-400">£{savings.physicalCost}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-body text-gray">Digital wallet cost</span>
                          <span className="text-h5 text-green-600">FREE</span>
                        </div>
                        <div className="border-t pt-4">
                          <div className="flex justify-between items-center">
                            <span className="text-body font-semibold text-dark">You save</span>
                            <span className="text-h4 text-green-600">£{savings.savings}/mo</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-green-50 rounded-xl p-4 text-center">
                        <FontAwesomeIcon icon="leaf" className="text-green-600 text-2xl mb-2" />
                        <div className="text-body font-bold text-dark">Eco-Friendly</div>
                        <div className="text-body-sm text-gray">Zero paper waste</div>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-4 text-center">
                        <FontAwesomeIcon icon="sync" className="text-blue-600 text-2xl mb-2" />
                        <div className="text-body font-bold text-dark">Always Current</div>
                        <div className="text-body-sm text-gray">Instant updates</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-white relative overflow-hidden" id="how-it-works">
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full filter blur-3xl opacity-50 -translate-y-1/2 -translate-x-1/2"></div>
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-gradient-to-l from-pink-100 to-purple-100 rounded-full filter blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2"></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="badge badge-purple inline-flex mb-4">
              <FontAwesomeIcon icon="route" />
              <span>How It Works</span>
            </div>
            
            <h2 className="text-h2 text-dark mb-6">
              Add to Wallet in <span className="text-gradient-blue-pink">3 Simple Steps</span>
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-400 via-purple-400 to-pink-400 -translate-x-1/2 hidden md:block"></div>
              
              <div className="space-y-12 md:space-y-16">
                {walletSteps.map((step, index) => (
                  <div
                    key={index}
                    className={`flex flex-col md:flex-row items-center gap-8 ${
                      index % 2 === 1 ? 'md:flex-row-reverse' : ''
                    }`}
                  >
                    <div className="flex-1 text-center md:text-left">
                      <div className="inline-flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-h5 shadow-lg">
                          {step.number}
                        </div>
                        <h3 className="text-h4 text-dark">{step.title}</h3>
                      </div>
                      <p className="text-body-lg text-gray">{step.description}</p>
                    </div>
                    
                    <div className="w-full md:w-1/2">
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 shadow-lg">
                        <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl flex items-center justify-center text-white mx-auto">
                          <FontAwesomeIcon icon={step.icon} className="text-3xl" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 md:p-16 text-white relative overflow-hidden shadow-2xl">
              <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
              <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl translate-x-1/2 -translate-y-1/2"></div>
              
              <div className="relative z-10 text-center">
                <h2 className="text-h2 text-white mb-6">
  Ready to Go Wallet-First?
</h2>
                <p className="text-body-lg text-white/90 mb-8 max-w-2xl mx-auto">
                  Join thousands of professionals who share their contact info the modern way. 
                  Download AM CARDMATE and add your card to your digital wallet today.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button 
                    onClick={() => setShowComingSoonPopup(true)}
                    className="btn btn-primary group w-full sm:w-auto"
                  >
                    <FontAwesomeIcon icon="download" />
                    <span>Get Started Today</span>
                    <FontAwesomeIcon 
                      icon="arrow-right" 
                      className="ml-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" 
                    />
                  </button>
                  
                  <a 
                    href="#how-it-works"
                    className="btn bg-white/20 backdrop-blur-sm text-white border-2 border-white/30 hover:bg-white hover:text-gray-900 transition-all duration-300 group w-full sm:w-auto"
                  >
                    <FontAwesomeIcon icon="info-circle" />
                    <span>Learn More</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="badge badge-pink inline-flex mb-4">
                <FontAwesomeIcon icon="shield-check" />
                <span>Security & Privacy</span>
              </div>
              
              <h2 className="text-h2 text-dark mb-6">
                Your Data, <span className="text-gradient-blue-pink">Your Control</span>
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FontAwesomeIcon icon="lock" className="text-blue-600 text-3xl" />
                </div>
                <h3 className="text-h5 text-dark mb-2">End-to-End Encrypted</h3>
                <p className="text-body text-gray">Your card data is encrypted and protected by your device's security</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FontAwesomeIcon icon="user-shield" className="text-purple-600 text-3xl" />
                </div>
                <h3 className="text-h5 text-dark mb-2">Privacy First</h3>
                <p className="text-body text-gray">Control what information you share and with whom</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FontAwesomeIcon icon="check-circle" className="text-green-600 text-3xl" />
                </div>
                <h3 className="text-h5 text-dark mb-2">Always Accessible</h3>
                <p className="text-body text-gray">Your card lives in your phone's wallet – available anytime, even without internet.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <div className="bg-gradient-to-b from-white to-blue-50/30">
        <Testimonials 
          customTitle="Professionals Love Wallet Integration" 
          customDescription="See how digital wallet cards are transforming networking"
          customBadgeText="Wallet Success Stories"
        />
      </div>
      
      <CTA 
        title="Ready to Add Your Card to Digital Wallets?"
        description="Download AM CARDMATE and join the wallet revolution. Share your business card with just a tap."
        primaryButtonText="Download Now"
        primaryButtonIcon="download"
        primaryButtonHref="https://apps.apple.com/pt/app/am-cardmate/id6747951046"
        secondaryButtonText="Learn More"
        secondaryButtonIcon="info-circle"
        secondaryButtonHref="#how-it-works"
        background="gradient"
      />
      
      {showComingSoonPopup && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowComingSoonPopup(false)}>
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 sm:w-20 h-16 sm:h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <FontAwesomeIcon icon="rocket" className="text-white text-2xl sm:text-3xl" />
              </div>
              
              <h3 className="text-h4 text-dark mb-4">Download AM CARDMATE</h3>
              <p className="text-body text-gray mb-8">
                AM CARDMATE is available on both App Store and Google Play!
              </p>
              
              <div className="grid grid-cols-1 gap-3 mb-6">
                <button 
                  onClick={() => window.open('https://apps.apple.com/pt/app/am-cardmate/id6747951046', '_blank')}
                  className="flex items-center gap-4 bg-black px-6 py-4 rounded-xl hover:bg-gray-900 transition-all"
                >
                  <FontAwesomeIcon icon={['fab', 'apple']} className="text-3xl text-white" />
                  <div className="text-left text-white">
                    <div className="text-caption">Download on the</div>
                    <div className="text-body-lg font-bold">App Store</div>
                  </div>
                </button>
                
                <button 
                  onClick={() => window.open('https://play.google.com/store/apps/details?id=com.amcardmate.app', '_blank')}
                  className="flex items-center gap-4 bg-black px-6 py-4 rounded-xl hover:bg-gray-900 transition-all"
                >
                  <FontAwesomeIcon icon={['fab', 'google-play']} className="text-3xl text-white" />
                  <div className="text-left text-white">
                    <div className="text-caption">GET IT ON</div>
                    <div className="text-body-lg font-bold">Google Play</div>
                  </div>
                </button>
              </div>
              
              <div className="space-y-3">
                <button className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all text-body" onClick={() => setShowComingSoonPopup(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DigitalWallet;